/**
 * Two independent sampling stages, both 100% outside production so
 * standalone/dev testing needs no setup. Manual override for either:
 *
 *   localStorage.setItem('hydra:rumSampling', 'always')  // force on
 *   localStorage.setItem('hydra:rumSampling', 'never')   // force off
 *
 * 1. Per page load (10%) — gates the whole RUM install, so an unsampled load
 *    reports nothing at all.
 * 2. Per transition (8%) — applied *within* an already-sampled load, because
 *    users navigate far more than they hard-load and each transition is its
 *    own POST (transitions don't batch the way requests do).
 *
 * The rates compound: ~0.8% of all production transitions report. The two are
 * deliberately different numbers so a rate is identifiable on sight. Raise the
 * transition rate once real volume is known — it starts low for validation.
 *
 * Both stages draw fresh rather than persisting a per-user decision: unbiased
 * for percentiles, and rate changes take effect immediately with no stale
 * draws. The page-load draw is memoized for the load (see
 * `isRumSessionSampled`); the transition draw is per transition.
 */

const OVERRIDE_STORAGE_KEY = 'hydra:rumSampling';
const PRODUCTION_SAMPLE_RATE = 0.1;
const PRODUCTION_TRANSITION_SAMPLE_RATE = 0.08;

type SamplingOptions = {
  sampleRate?: number;
  random?: () => number;
};

/** 'always' | 'never' | null — null when unset or storage is unavailable. */
function readOverride(): 'always' | 'never' | null {
  try {
    const override = localStorage.getItem(OVERRIDE_STORAGE_KEY);
    if (override === 'always' || override === 'never') {
      return override;
    }
  } catch {
    // storage unavailable (privacy mode) — fall through to the sample rate
  }
  return null;
}

function draw(options: SamplingOptions, productionRate: number): boolean {
  const override = readOverride();
  if (override != null) {
    return override === 'always';
  }
  const sampleRate = options.sampleRate ?? (__CIO_ENV__ === 'production' ? productionRate : 1);
  return (options.random ?? Math.random)() < sampleRate;
}

export function shouldSampleRumSession(options: SamplingOptions = {}): boolean {
  return draw(options, PRODUCTION_SAMPLE_RATE);
}

/**
 * Whether to report ONE transition, drawn per transition on a page load that
 * already passed `isRumSessionSampled`. Not memoized: each navigation is an
 * independent draw, so a heavy navigator contributes proportionally more
 * transitions — which is what "what does a transition cost" asks. The
 * `always` override forces every transition through, so manual testing in
 * production sees them all.
 */
export function shouldSampleRumTransition(options: SamplingOptions = {}): boolean {
  return draw(options, PRODUCTION_TRANSITION_SAMPLE_RATE);
}

let sessionDecision: boolean | null = null;

/**
 * The page load's sampling decision, drawn once and shared by every beacon
 * kind. `shouldSampleRumSession` rolls fresh each call — using it directly
 * from more than one beacon would let the initial-load and requests beacons
 * silently disagree on which page loads report.
 */
export function isRumSessionSampled(): boolean {
  sessionDecision ??= shouldSampleRumSession();
  return sessionDecision;
}
